import React, {createContext, useContext, useState, useEffect, PropsWithChildren} from 'react';
import {jwtDecode} from "jwt-decode";
// Both come from the public `aws-amplify/*` entry points, not the internal
// `@aws-amplify/core` and `@aws-amplify/auth` packages this used to reach into.
// Every other module here already used the public ones; mixing the two is how you
// end up talking to a different Amplify instance than the one Amplify.configure()
// set up, and the failure mode is silent — a Hub that never delivers `signedIn`.
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession } from 'aws-amplify/auth';
import { initPostHog, posthog } from "../utils/posthog";
import { getConsentState } from "../utils/consent";
import { setConversionUserId } from "../utils/conversions";
import { ConsentBanner } from "../component/consent/ConsentBanner";

export enum UserGroup {
    LOCALINGOS_ADMIN = "localingos-admin",
}

// Define the structure of the user data, including the API key
interface UserType {
    id?: string;
    email?: string;
    name?: string;
    picture?: string;
    email_verified?: string;
    groups?: Set<UserGroup>;
    isAdmin?: boolean;
}

/**
 * Whether the Cognito session lookup has settled yet.
 *
 * 'checking' is only ever true before the first `fetchAuthSession()` resolves.
 * Consumers that need to distinguish "definitely signed out" from "not known yet"
 * must read this rather than inferring it from an empty `user.email` — see
 * NavigationComponent, which uses it to avoid rendering the signed-out marketing
 * shell over a signed-in user's dashboard URL.
 */
export type AuthStatus = 'checking' | 'authenticated' | 'anonymous';

// Creating the context with an undefined initial value
const UserContext = createContext<UserType | undefined>(undefined);
const AuthStatusContext = createContext<AuthStatus>('checking');

// UserProvider component
export const UserProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [userData, setUserData] = useState<UserType>({});
    const [authStatus, setAuthStatus] = useState<AuthStatus>('checking');

    useEffect(() => {
        // Defined inside the effect because nothing outside it uses them and the
        // effect must run exactly once (see the empty dependency array below). Hoisting
        // them to the component body would make them new identities every render and
        // force this one-shot bootstrap effect to depend on them.

        /** No session, or one without an id token: a genuinely anonymous visitor. */
        const markAnonymous = () => {
            // Don't call posthog.reset() here: this branch also runs on routine page
            // loads for visitors who have never signed in, and reset() would rotate
            // their anonymous distinct_id every load, breaking session continuity.
            // reset() belongs to the signedOut Hub event, which is a real transition.
            setUserData({});
            setConversionUserId(undefined);
            setAuthStatus('anonymous');
        };

        const fetchUserData = async () => {
            try {
                const session = await fetchAuthSession();
                const idToken = session.tokens?.idToken;
                if (!idToken) {
                    // Checked rather than asserted with `!`. The assertion turned an
                    // absent token into a TypeError that the catch below reported as
                    // 'anonymous', so a session that merely had not produced tokens yet
                    // was indistinguishable from a signed-out visitor — and from a
                    // genuine failure to reach Cognito.
                    markAnonymous();
                    return;
                }
                const jwt     = parseJwtToken(idToken.toString());
                const groups = new Set(!jwt ? [] : (jwt['cognito:groups'] || []).filter(
                    (g:string): g is UserGroup => Object.values(UserGroup).includes(g as UserGroup)
                ));
                setUserData({
                    id:             jwt?.sub,
                    email:          jwt?.email,
                    name:           jwt?.name,
                    picture:        jwt?.picture,
                    email_verified: jwt?.email_verified,
                    groups:         groups,
                    isAdmin:        groups.has(UserGroup.LOCALINGOS_ADMIN),
                });
                // Link this authenticated user to their prior anonymous session
                // in PostHog. The Cognito `sub` is stable across logins, so it's
                // the right identifier to use. Prior anonymous session replays
                // and events will be automatically merged into this Person.
                if (jwt?.sub) {
                    posthog.identify(jwt.sub, {
                        email:    jwt.email,
                        name:     jwt.name,
                        is_admin: groups.has(UserGroup.LOCALINGOS_ADMIN),
                    });
                }
                // The `project_created` conversion is observed from apiClient's response
                // interceptor, which has no access to React context, and it needs the
                // user's identity to fire once per account rather than once per project.
                setConversionUserId(jwt?.sub);
                setAuthStatus('authenticated');
            } catch (error) {
                // Reaching here now means the lookup itself failed — misconfiguration, or
                // Cognito being unreachable — because an empty session is handled above.
                // The visitor still has to be treated as signed out, but silently doing so
                // is what made an auth outage look like a routine sign-out. Say so.
                // eslint-disable-next-line no-console
                console.error('[auth] session lookup failed; treating visitor as signed out.', error);
                markAnonymous();
            }
        };

        // PostHog needs exactly one init before any capture() or identify() call
        // does anything. It starts opted out unless the visitor has already
        // accepted, and ConsentBanner opts it in when they do.
        initPostHog(getConsentState() === 'granted');

        // initial load
        fetchUserData();

        // subscribe to auth events
        const removeListener = Hub.listen('auth', ({ payload }) => {
            const { event } = payload;
            if (event === 'signedIn' || event === 'signInWithRedirect') {
                // signedIn covers the password flow; signInWithRedirect fires when
                // Amplify finishes exchanging the ?code= from a Google/OAuth callback.
                // Without the second case a federated sign-in updated no React state
                // until something else happened to re-trigger a session lookup, which
                // is why it took a second trip through the sign-in UI to appear.
                fetchUserData();
            } else if (event === 'signInWithRedirect_failure') {
                // The OAuth callback could not be completed. Surface it — this is the
                // path that used to fail silently and leave a stale ?code= in the URL.
                // eslint-disable-next-line no-console
                console.error('[auth] OAuth sign-in could not be completed.',
                    'error' in payload.data ? payload.data.error : payload);
                markAnonymous();
            } else if (event === 'signedOut') {
                // Break the analytics link to the previously identified user
                // before re-fetching, so any subsequent anonymous activity
                // is recorded against a fresh distinct_id.
                posthog.reset();
                fetchUserData();
            }
        });

        return () => {
            removeListener();
        };
    }, []);

    // Children render immediately, before the session lookup settles.
    //
    // This provider used to return a bare `<div>Loading...</div>` until
    // `fetchAuthSession()` resolved. Because that happens after main.js has been
    // downloaded and executed, every visitor — including the anonymous ones the
    // marketing pages are prerendered for — watched the fully rendered page get
    // replaced by the word "Loading..." and then reappear. Auth state is not
    // needed to render marketing content, so it must not gate it; the routes that
    // genuinely require a session gate on `useAuthStatus()` instead.
    // The consent banner is mounted here because UserProvider is the outermost
    // component that wraps every route — marketing, dashboard and auth alike — and
    // mounts exactly once per page load. It renders unconditionally and is shown or
    // hidden by the `data-consent` attribute resolved before the first paint, so
    // its position in the tree affects nothing but where the (position: fixed)
    // element sits in the DOM.
    return (
        <AuthStatusContext.Provider value={authStatus}>
            <UserContext.Provider value={userData}>
                {children}
                <ConsentBanner />
            </UserContext.Provider>
        </AuthStatusContext.Provider>
    );
};

// Custom hook to use the UserContext
export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

/**
 * Progress of the Cognito session lookup.
 *
 * `useUser()` returns `{}` both while the lookup is in flight and when it has
 * concluded the visitor is anonymous. Anything that would show or hide signed-in
 * UI needs to tell those apart, or it will flash the signed-out state at a
 * signed-in user on every page load.
 */
export const useAuthStatus = (): AuthStatus => useContext(AuthStatusContext);

interface JwtToken {
    sub: string;
    'cognito:username': string;
    email: string;
    'custom:company': string;
    name: string;
    picture?: string;
    email_verified: string;
    'custom:apikey': string;
    'cognito:groups': string[];
}

function parseJwtToken(token: string): JwtToken | undefined {
    try {
        return jwtDecode<JwtToken>(token!);
    } catch (error) {
        console.error("Failed to decode token or extract custom field:", error);
    }
}
